import { Link } from "react-router-dom";
import { Clock, Layers, User, ArrowRight, Star } from "lucide-react";

const levelColor = {
  Beginner: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Intermediate: "text-blue-700 bg-blue-50 border-blue-200",
  Advanced: "text-amber-700 bg-amber-50 border-amber-200",
};

export function CourseCard({ course }) {
  const free = course.price === 0;
  return (
    <Link
      to={`/courses/${course.slug}`}
      data-testid={`course-card-${course.slug}`}
      className="group flex flex-col bg-white border border-border rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
    >
      <div className="relative h-44 overflow-hidden bg-navy grain">
        {course.image ? (
          <img src={course.image} alt={course.title} loading="lazy" referrerPolicy="no-referrer"
            className="h-full w-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="h-full w-full flex items-center justify-center"><span className="font-display text-gold text-lg font-bold">KRV Prime</span></div>
        )}
        <span className={`absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-md border ${levelColor[course.level] || "bg-white"}`}>
          {course.level}
        </span>
        {course.status === "coming_soon" && (
          <span className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-md bg-navy text-gold">Coming Soon</span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-5">
        <p className="eyebrow text-slate-400 mb-2">{course.category}</p>
        <h3 className="font-display text-lg font-semibold text-navy leading-snug group-hover:text-navy-light">{course.title}</h3>
        <p className="mt-2 text-sm text-slate-500 line-clamp-2 flex-1">{course.subtitle}</p>
        <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{course.duration}</span>
          <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{course.modules_count} lessons</span>
          {course.rating && <span className="inline-flex items-center gap-1 text-amber-600"><Star className="h-3.5 w-3.5 fill-amber-500" />{course.rating}</span>}
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
            {free ? "Free" : `₹${course.price.toLocaleString("en-IN")}`}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-navy group-hover:text-gold transition-colors">
            View Course <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function InsightCard({ report }) {
  const date = new Date(report.published_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const thumb = report.featured_image ? `${process.env.REACT_APP_BACKEND_URL}${report.featured_image}` : null;
  const stamp = report.created_at || report.updated_at || report.published_date;
  const isNew = stamp && (Date.now() - new Date(stamp).getTime()) < 24 * 60 * 60 * 1000;
  return (
    <Link
      to={`/research/${report.slug}`}
      data-testid={`research-card-${report.slug}`}
      className="group flex flex-col bg-white border border-border rounded-lg overflow-hidden hover:shadow-lg hover:border-navy/20 transition-all duration-300"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-navy" data-testid={`research-thumb-${report.slug}`}>
        {isNew && (
          <span
            data-testid={`research-new-badge-${report.slug}`}
            className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-gold px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-navy shadow-md"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-navy animate-pulse" />
            New
          </span>
        )}
        {thumb ? (
          <img
            src={thumb}
            alt={report.title}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="relative h-full w-full grain flex flex-col justify-between p-5 bg-gradient-to-br from-navy via-navy to-[#0d2140] overflow-hidden">
            <div className="absolute -right-3 -bottom-2 flex items-end gap-1.5 opacity-25" aria-hidden="true">
              {[16, 26, 20, 34, 28, 44].map((h, i) => (
                <span key={i} style={{ height: h }} className="w-3 rounded-sm bg-gold" />
              ))}
            </div>
            <div className="relative flex items-center justify-between">
              <span className="text-[11px] font-bold tracking-wide text-white/90"><span className="text-gold">KRV</span>FinMart</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-gold/40 text-gold">{report.index_tag || report.category}</span>
            </div>
            <span className="relative font-display text-white text-lg font-semibold leading-snug line-clamp-3">{report.title}</span>
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-subtle border border-border text-navy">{report.category}</span>
          {report.index_tag && <span className="text-[11px] text-slate-400">{report.index_tag}</span>}
        </div>
        <h3 className="font-display text-xl font-semibold text-navy leading-snug group-hover:text-navy-light line-clamp-2">{report.title}</h3>
        <div className="flex-1" />
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border text-xs text-slate-500">
          <span className="inline-flex items-center gap-3">
            <span>{date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{report.reading_time}</span>
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-navy group-hover:text-gold transition-colors">
            Read Analysis <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCard({ article }) {
  const date = new Date(article.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  return (
    <Link
      to={`/knowledge/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className="group flex flex-col border-b border-border py-6 hover:bg-subtle/60 transition-colors px-2 -mx-2 rounded-md"
    >
      <p className="eyebrow text-gold mb-2">{article.category}</p>
      <h3 className="font-display text-lg font-semibold text-navy group-hover:text-navy-light">{article.title}</h3>
      <p className="mt-2 text-sm text-slate-500 line-clamp-2">{article.excerpt}</p>
      <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
        <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{article.author}</span>
        <span>{date}</span>
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{article.reading_time}</span>
      </div>
    </Link>
  );
}
